import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { BrandCard } from '../components/shared/BrandCard';
import { FeaturedBrandsCarousel } from '../components/home/FeaturedBrandsCarousel';
import { Pagination } from '../components/shared/Pagination';
import { SearchIcon, FilterIcon, SlidersHorizontalIcon } from 'lucide-react';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
export function BrandsPage() {
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const ITEMS_PER_PAGE = 12;
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const categories = [{
    id: 'all',
    name: 'All Brands'
  }, {
    id: 'sports',
    name: 'Sports Nutrition'
  }, {
    id: 'beauty',
    name: 'Beauty & Skincare'
  }, {
    id: 'baby',
    name: 'Baby & Mother'
  }, {
    id: 'vitamins',
    name: 'Vitamins & Supplements'
  }, {
    id: 'personal',
    name: 'Personal Care'
  }];
  const brands = [{
    id: 1,
    name: 'Optimum Nutrition',
    logo: 'https://drnutrition.com/storage/media/HZeOJn0BLQ7DySApaV4NzYo9qUa9pvdDC6XWIAGz.jpg',
    category: 'sports',
    productCount: 156,
    discount: 40,
    description: "World's #1 Sports Nutrition Brand",
    isFeatured: true
  }, {
    id: 2,
    name: 'Dymatize',
    logo: 'https://drnutrition.com/storage/media/gJh3RduxB5CzXXia5UnfphESCeOVVZUnU5cRUa5e.jpg',
    category: 'sports',
    productCount: 98,
    discount: 35,
    description: 'Do. More.',
    isFeatured: true
  }, {
    id: 3,
    name: 'BSN',
    logo: 'https://drnutrition.com/storage/media/cJUkR6p9YDficbZ1QMsh7dzv6VGsnS70aF7KA3Ju.jpg',
    category: 'sports',
    productCount: 87,
    discount: 38,
    description: 'Finish First',
    isFeatured: true
  }, {
    id: 4,
    name: 'MuscleTech',
    logo: 'https://drnutrition.com/storage/media/ohK7cXWJCyXpWD1XZ1Dra2VxgeL0SOMtqqzY0d6Y.jpg',
    category: 'sports',
    productCount: 134,
    discount: 42,
    description: 'Science-Backed Performance',
    isFeatured: true
  }, {
    id: 5,
    name: 'Rule One',
    logo: 'https://drnutrition.com/storage/media/80hofbNplzbnRGieNklSpLGRvBbpqfSbt7Pa5djO.jpg',
    category: 'sports',
    productCount: 67,
    discount: 30,
    description: 'Premium Quality Supplements',
    isFeatured: false
  }, {
    id: 6,
    name: 'Quest',
    logo: 'https://drnutrition.com/storage/media/ECrf20MUTvnylb0GdJTADL6UWLc7Y6f5DqhdJPoU.jpg',
    category: 'sports',
    productCount: 89,
    discount: 33,
    description: 'Clean Label Nutrition',
    isFeatured: false
  }, {
    id: 7,
    name: 'Muscleblaze',
    logo: 'https://drnutrition.com/storage/media/9D7Zgd5M2kvc3NXBWWXWcVvCnFpuqoX7JtNHFJA2.jpg',
    category: 'sports',
    productCount: 112,
    discount: 36,
    description: "India's #1 Supplement Brand",
    isFeatured: false
  }, {
    id: 8,
    name: 'C4',
    logo: 'https://drnutrition.com/storage/media/GgpcD7Kj8wsqvX6Ug4fRwL39sgl7sOF69vLdMR7x.jpg',
    category: 'sports',
    productCount: 76,
    discount: 35,
    description: 'Americas #1 Pre-Workout',
    isFeatured: false
  }, {
    id: 9,
    name: 'Centrum',
    logo: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400',
    category: 'vitamins',
    productCount: 45,
    discount: 25,
    description: 'Complete Multivitamin',
    isFeatured: false
  }, {
    id: 10,
    name: 'Nature Made',
    logo: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400',
    category: 'vitamins',
    productCount: 78,
    discount: 30,
    description: '#1 Pharmacist Recommended',
    isFeatured: false
  }, {
    id: 11,
    name: 'La Roche-Posay',
    logo: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400',
    category: 'beauty',
    productCount: 92,
    discount: 35,
    description: 'Dermatologist Recommended',
    isFeatured: false
  }, {
    id: 12,
    name: 'Cetaphil',
    logo: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400',
    category: 'beauty',
    productCount: 56,
    discount: 28,
    description: 'Gentle Skin Care',
    isFeatured: false
  }, {
    id: 13,
    name: 'Similac',
    logo: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=400',
    category: 'baby',
    productCount: 34,
    discount: 20,
    description: 'Trusted Baby Nutrition',
    isFeatured: false
  }, {
    id: 14,
    name: 'Pampers',
    logo: 'https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400',
    category: 'baby',
    productCount: 67,
    discount: 25,
    description: '#1 Choice of Hospitals',
    isFeatured: false
  }, {
    id: 15,
    name: 'Neutrogena',
    logo: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400',
    category: 'beauty',
    productCount: 89,
    discount: 32,
    description: 'Dermatologist Recommended',
    isFeatured: false
  }, {
    id: 16,
    name: 'Solgar',
    logo: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=400',
    category: 'vitamins',
    productCount: 123,
    discount: 35,
    description: 'Gold Standard Supplements',
    isFeatured: false
  }, {
    id: 17,
    name: 'Garden of Life',
    logo: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400',
    category: 'vitamins',
    productCount: 98,
    discount: 30,
    description: 'Organic Supplements',
    isFeatured: false
  }, {
    id: 18,
    name: 'NOW Foods',
    logo: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400',
    category: 'vitamins',
    productCount: 156,
    discount: 28,
    description: 'Natural Products',
    isFeatured: false
  }, {
    id: 19,
    name: 'Vichy',
    logo: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=400',
    category: 'beauty',
    productCount: 76,
    discount: 38,
    description: 'Thermal Spa Water',
    isFeatured: false
  }, {
    id: 20,
    name: 'Enfamil',
    logo: 'https://images.unsplash.com/photo-1611312449408-fcece27cdbb7?w=400',
    category: 'baby',
    productCount: 45,
    discount: 22,
    description: 'Expert in Infant Nutrition',
    isFeatured: false
  }, {
    id: 21,
    name: 'Dove',
    logo: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400',
    category: 'personal',
    productCount: 67,
    discount: 25,
    description: 'Real Beauty Care',
    isFeatured: false
  }, {
    id: 22,
    name: 'Johnson & Johnson',
    logo: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400',
    category: 'baby',
    productCount: 89,
    discount: 30,
    description: 'Trusted Family Care',
    isFeatured: false
  }, {
    id: 23,
    name: 'Cellucor',
    logo: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400',
    category: 'sports',
    productCount: 54,
    discount: 33,
    description: 'Performance Nutrition',
    isFeatured: false
  }, {
    id: 24,
    name: 'Nordic Naturals',
    logo: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400',
    category: 'vitamins',
    productCount: 72,
    discount: 35,
    description: 'Pure Omega-3s',
    isFeatured: false
  }];
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const filteredBrands = brands.filter(brand => {
    const matchesSearch = brand.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || brand.category === selectedCategory;
    const matchesLetter = selectedLetter === 'all' || brand.name.charAt(0).toUpperCase() === selectedLetter;
    return matchesSearch && matchesCategory && matchesLetter;
  });
  const sortedBrands = useMemo(() => {
    return [...filteredBrands].sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'products') return b.productCount - a.productCount;
      if (sortBy === 'discount') return b.discount - a.discount;
      return 0;
    });
  }, [filteredBrands, sortBy]);
  // Pagination calculations
  const totalPages = Math.ceil(sortedBrands.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBrands = sortedBrands.slice(startIndex, endIndex);
  return <HelmetProvider data-id="element-6141" data-mp="2mzgzvv8di5oi">
      <SEO title="Shop by Brand" description="Explore top health, beauty, and wellness brands at Innova Pharmacy. Shop authentic products from leading international brands with fast delivery across Saudi Arabia." canonical="/en/brands/" hreflangAr="/brands/" hreflangEn="/en/brands/" hreflangDefault="/brands/" keywords="pharmacy brands Saudi Arabia, health brands, beauty brands, supplement brands, authentic products, international brands" data-id="element-6142" data-mp="2myxrb54m4tn5" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6143" data-mp="h0qgrovrkb78">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-6144" data-mp="c2x7uo32t339">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-6145" data-mp="c0khpnn5zmf4" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-6146" data-mp="3ua4kr1u2ay98">
          {/* Mobile Header Overlay */}
          <MobileInternalHeader title="Shop by Brand" data-id="element-6147" data-mp="1xdnnumdq7obx" />
          <h1 className="sr-only" data-id="element-6148" data-mp="1si8b5xrgfyh3">Shop by Brand - Top Healthcare Brands</h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Brands'
        }]} title="Shop by Brand" description="Explore top health, beauty, and wellness brands. Shop authentic products from leading international brands with fast delivery across Saudi Arabia." data-id="element-6149" data-mp="1zzwnnuq6c254" />
          <div className="container mx-auto px-4 py-8" data-id="element-6150" data-mp="1m0euz49rc4qh">
            {/* Stats Bar */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-id="element-6151" data-mp="1qzrhfnwpv9sj">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-6152" data-mp="1qygfzuqque4l">
                <div data-id="element-6153" data-mp="1vzmbu1fpahic">
                  <div className="text-3xl font-bold text-[#1E2B55] mb-1" data-id="element-6154" data-mp="1vwrnhytkgcjn">
                    {brands.length}+
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-6155" data-mp="37drij27at5hd">Brands</div>
                </div>
                <div data-id="element-6156" data-mp="17bi492dbygsm">
                  <div className="text-3xl font-bold text-[#1E2B55] mb-1" data-id="element-6157" data-mp="17ayw2ddhf6rs">
                    2000+
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-6158" data-mp="12c5ikky73frm">Products</div>
                </div>
                <div data-id="element-6159" data-mp="1gb58dvxg20pd">
                  <div className="text-3xl font-bold text-green-600 mb-1" data-id="element-6160" data-mp="1gbnah96sf957">
                    Up to 40%
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-6161" data-mp="ola7r344yole">Discount</div>
                </div>
                <div data-id="element-6162" data-mp="jm5xsa9e0mu0">
                  <div className="text-3xl font-bold text-purple-600 mb-1" data-id="element-6163" data-mp="jmpfbpv4l97i">
                    100%
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-6164" data-mp="xedrtu9adzp0">Authentic</div>
                </div>
              </div>
            </div>
            {/* Featured Brands Carousel */}
            <div className="mb-8" data-id="element-6165" data-mp="4wrlatailpae">
              <FeaturedBrandsCarousel brands={brands.filter(b => b.isFeatured)} data-id="element-6166" data-mp="4vqampxev4t7" />
            </div>
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-id="element-6167" data-mp="3mwvkie9adk8s">
              {/* Search Bar */}
              <div className="mb-6" data-id="element-6168" data-mp="3ro3tj96pf3lf">
                <div className="relative" data-id="element-6169" data-mp="3rp6lzljode3g">
                  <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-6170" data-mp="3czhi3ik76vzh" />
                  <input type="text" placeholder="Search brands..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-6171" data-mp="3hx95q0dt7vxv" />
                </div>
              </div>
              {/* Category Filters */}
              <div className="mb-6" data-id="element-6172" data-mp="2k9sgb2znyrwt">
                <div className="flex items-center gap-2 mb-3" data-id="element-6173" data-mp="2k8qrlvpq66wm">
                  <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-6174" data-mp="2p9wn21ars89u" />
                  <span className="font-medium text-gray-700" data-id="element-6175" data-mp="2p70ocar3lfkc">
                    Filter by Category
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-6176" data-mp="30ortquda8p2f">
                  {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedCategory === category.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6177" data-mp="2vrhvcp7x132g">
                      {category.name}
                    </button>)}
                </div>
              </div>
              {/* Alphabet Navigation */}
              <div className="mb-6" data-id="element-6178" data-mp="39hv06ipig1nk">
                <div className="flex items-center gap-2 mb-3" data-id="element-6179" data-mp="231sqg0s34ivk">
                  <SlidersHorizontalIcon className="w-5 h-5 text-gray-600" data-id="element-6180" data-mp="22zy5wnkq6ss0" />
                  <span className="font-medium text-gray-700" data-id="element-6181" data-mp="1y1ejyn64tgzn">Browse A-Z</span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-6182" data-mp="dmi3y514k46g">
                  <button onClick={() => setSelectedLetter('all')} className={`w-10 h-10 rounded-lg font-medium transition-all ${selectedLetter === 'all' ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6183" data-mp="ijqm45m9w87p">
                    All
                  </button>
                  {alphabet.map(letter => <button key={letter} onClick={() => setSelectedLetter(letter)} className={`w-10 h-10 rounded-lg font-medium transition-all ${selectedLetter === letter ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-6184" data-mp="11a81l8i3h7b4">
                      {letter}
                    </button>)}
                </div>
              </div>
              {/* Sort Options */}
              <div className="flex items-center justify-between" data-id="element-6185" data-mp="34zcvm12jvom5">
                <div className="text-sm text-gray-600" data-id="element-6186" data-mp="34zmj9i04hjoo">
                  Showing{' '}
                  <span className="font-medium" data-id="element-6187" data-mp="30zc35cim9cw3">
                    {startIndex + 1}-{Math.min(endIndex, sortedBrands.length)}
                  </span>{' '}
                  of <span className="font-medium" data-id="element-6188" data-mp="39xwflvxl2qbv">{sortedBrands.length}</span>{' '}
                  brands
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" data-id="element-6189" data-mp="3nww515sx7993">
                  <option value="name" data-id="element-6190" data-mp="3sp71fyogccww">Sort by Name</option>
                  <option value="products" data-id="element-6191" data-mp="3sr0djthu3pca">Most Products</option>
                  <option value="discount" data-id="element-6192" data-mp="bhv8nk0ap5s1">Best Discount</option>
                </select>
              </div>
            </div>
            {/* Brands Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8" data-id="element-6193" data-mp="q3itl7eboeby">
              {paginatedBrands.map(brand => <BrandCard key={brand.id} brand={brand} data-id="element-6194" data-mp="u42t2hmdbu19" />)}
            </div>
            {/* No Results */}
            {sortedBrands.length === 0 && <div className="text-center py-12" data-id="element-6195" data-mp="l45wlttqlqvn">
                <div className="text-6xl mb-4" data-id="element-6196" data-mp="l2uurzjuqt76">🔍</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-6197" data-mp="6f00ieq74grz">
                  No brands found
                </h3>
                <p className="text-gray-600" data-id="element-6198" data-mp="b79vi9664714">
                  Try adjusting your search or filters
                </p>
              </div>}
            {/* Pagination */}
            {sortedBrands.length > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} data-id="element-6199" data-mp="2e6bfi10ovxi" />}
          </div>
        </main>
        <Footer data-id="element-6200" data-mp="3joe801npdlyq" />
        <MobileFooterNav data-id="element-6201" data-mp="3okv53lcvrany" />
        <PharmacistChat data-id="element-6202" data-mp="3ok3aodv0riz9" />
        <div className="h-16 md:hidden" data-id="element-6203" data-mp="3okd122jz7s5h"></div>
      </div>
    </HelmetProvider>;
}